import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  PARAM_KEYS,
  PL, ADVANCED_PL,
  PL_SHORT,
  STAGES_ORDER,
  STAGE_SHORT,
  getScoreColor,
} from "@/lib/reportUtils";

const bandFor = (v) => {
  if (v >= 7) return { label: "High", color: "#43A047" };
  if (v >= 4) return { label: "Moderate", color: "#F57F17" };
  return { label: "Low", color: "#D32F2F" };
};

/**
 * 12 donut gauges — one per parameter — replacing the previous bar-chart grid.
 * Each donut shows the parameter's average adjusted score across all stages on
 * a 0–10 scale, color-coded green / orange / red, with the relative band label
 * (Low / Moderate / High). Per-stage values appear in a compact secondary row
 * underneath so the data is visible in PDF without relying on hover.
 *
 * No calculations are changed — values come straight from
 * `paramsByStage[stage][param].adjusted` (already computed by the engine).
 */
export const ParameterDonuts = ({ paramsByStage, mode }) => {
  const scoreScale = mode === "advanced" ? 10 : 1;
  const activeStages = STAGES_ORDER.filter((s) => paramsByStage[s]);

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
      data-testid="param-donuts"
    >
      {PARAM_KEYS.map((pk) => {
        const stageVals = activeStages.filter(s => paramsByStage[s]?.[pk]?.adjusted != null).map((s) => ({
          stage: s,
          stageShort: STAGE_SHORT[s],
          v: +((paramsByStage[s]?.[pk]?.adjusted) || 0).toFixed(2),
        }));
        const valid = stageVals.filter((s) => s.v > 0 || s.v === 0);
        const avg =
          valid.length > 0
            ? valid.reduce((a, b) => a + b.v, 0) / valid.length
            : 0;
        const band = valid.length ? bandFor(avg) : {label:'N/A',color:'#617476'};
        const filled = Math.min(10, Math.max(0, avg));
        const data = [
          { name: "score", value: filled, fill: band.color },
          { name: "rest", value: Math.max(0, 10 - filled), fill: "#EEF1EE" },
        ];

        return (
          <div
            key={pk}
            className="parameter-donut-card bg-white rounded-lg border border-[#E0E6E1] p-3 flex flex-col items-center"
            data-testid={`param-donut-${pk}`}
          >
            <p
              className="parameter-donut-title text-[11px] font-bold text-[#1B291D] truncate w-full text-center"
              title={(mode === "advanced" ? ADVANCED_PL : PL)[pk]}
            >
              {PL_SHORT[pk]}
            </p>

            <div className="parameter-donut-gauge relative" style={{ width: 110, height: 80 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="100%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius={42}
                    outerRadius={62}
                    dataKey="value"
                    isAnimationActive={false}
                    stroke="none"
                  >
                    {data.map((d, i) => (
                      <Cell key={i} fill={d.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {/* Centered value */}
              <div
                className="parameter-donut-score absolute inset-x-0 bottom-0 text-center"
                style={{ pointerEvents: "none" }}
              >
                <p
                  className="text-[20px] leading-[1.1] font-light"
                  style={{ color: band.color }}
                  data-testid={`param-donut-value-${pk}`}
                >
                  {valid.length ? (avg * scoreScale).toFixed(2) : "N/A"}
                </p>
                <p className="mt-0.5 text-[8px] leading-[1.2] text-[#617364] tracking-wider uppercase">
                  / {10 * scoreScale}
                </p>
              </div>
            </div>

            <span
              className="parameter-donut-status mt-1 inline-flex items-center justify-center text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
              style={{
                color: band.color,
                backgroundColor: band.color + "1A",
                border: `1px solid ${band.color}33`,
              }}
            >
              {band.label}
            </span>

            {/* Per-stage mini bar chart — keeps detail visible in PDF */}
            <div
              className="parameter-mini-chart mt-2 w-full pt-2 border-t border-[#E0E6E1]"
              data-testid={`param-donut-bars-${pk}`}
            >
              <div className="parameter-mini-bars flex items-stretch justify-between gap-1" style={{ height: 38 }}>
                {stageVals.map((s) => {
                  const pct = Math.max(4, Math.min(100, (s.v / 10) * 100));
                  return (
                    <div
                      key={s.stage}
                      className="parameter-mini-column flex-1 flex flex-col items-center min-w-0"
                      style={{ height: "100%" }}
                      title={`${s.stage}: ${(s.v * scoreScale).toFixed(2)} / ${10 * scoreScale}`}
                    >
                      <p
                        className="parameter-mini-value h-3 flex items-center justify-center text-[8px] font-semibold leading-none"
                        style={{ color: getScoreColor(s.v) }}
                      >
                        {(s.v * scoreScale).toFixed(1)}
                      </p>
                      <div className="parameter-mini-track w-full flex-1 flex items-end">
                        <div
                          className="parameter-mini-fill w-full rounded-sm"
                          style={{
                            height: `${pct}%`,
                            minHeight: 2,
                            backgroundColor: getScoreColor(s.v),
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="parameter-mini-labels mt-1 flex justify-between gap-1">
                {stageVals.map((s) => (
                  <p
                    key={s.stage}
                    className="parameter-mini-label flex-1 min-h-[10px] text-[7px] leading-tight text-[#617364] text-center truncate uppercase tracking-wider"
                    title={s.stage}
                  >
                    {s.stageShort}
                  </p>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
